/**
 * /tools/woop?clientId=X — STP-108.
 *
 * Step 3 4-field structured tool: Wish · Outcome · Obstacle · Plan.
 * Persists generic tracker only — payload-in-`tool_completions.notes`
 * covers it (no dedicated table for the pilot).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';

const FIELDS = ['wish', 'outcome', 'obstacle', 'plan'] as const;
type Field = (typeof FIELDS)[number];

type FormState = Record<Field, string>;
const DEFAULT_FORM: FormState = { wish: '', outcome: '', obstacle: '', plan: '' };

export function WoopPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken, user } = useAuth();
  const clientId = useClientId();

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const existing = await api.getToolCompletion(accessToken, clientId, 'woop');
        if (!active) return;
        if (existing?.payload && typeof existing.payload === 'object') {
          const p = existing.payload as Record<string, unknown>;
          const next: FormState = { ...DEFAULT_FORM };
          FIELDS.forEach((f) => {
            if (typeof p[f] === 'string') next[f] = p[f];
          });
          setForm(next);
          setSavedAt(existing.completed_at ?? null);
        }
      } catch {
        /* swallow */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  const allFilled = useMemo(() => FIELDS.every((f) => form[f].trim().length > 0), [form]);
  const canSubmit = !submitting && hydrated && allFilled;

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!user) {
      setSubmitError(t('tools.woop.errors.notAuthenticated'));
      return;
    }
    if (!clientId) {
      setSubmitError(t('tools.woop.errors.missingClient'));
      return;
    }
    if (!allFilled) {
      setSubmitError(t('tools.woop.errors.incomplete'));
      return;
    }
    setSubmitting(true);
    const payload = {
      wish: form.wish.trim(),
      outcome: form.outcome.trim(),
      obstacle: form.obstacle.trim(),
      plan: form.plan.trim(),
    };
    try {
      const completion = await api.submitToolCompletion(accessToken, clientId, 'woop', {
        payload,
      });
      setSavedAt(completion.completed_at ?? new Date().toISOString());
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.woop.errors.generic'));
      } else {
        setSubmitError(t('tools.woop.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section className="max-w-2xl mx-auto px-s5 py-s8" data-testid="woop-missing-client">
        <h1 className="font-display text-display-lg text-ink">{t('tools.woop.title')}</h1>
        <p className="mt-s3 text-flame-ember">{t('tools.woop.errors.missingClient')}</p>
      </section>
    );
  }

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2"><PrintButton /></div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.woop.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">{t('tools.woop.title')}</h1>
        <p className="mt-s3 text-gray-2 max-w-prose">{t('tools.woop.description')}</p>
      </header>

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s5"
        noValidate
      >
        {FIELDS.map((field) => (
          <article
            key={field}
            className="rounded-lg border border-bone-3 bg-bone-2 p-s5"
            data-testid={`woop-${field}-card`}
          >
            <label htmlFor={`woop-${field}`} className="block">
              <h2 className="font-display text-display-sm text-ink">
                {t(`tools.woop.fields.${field}.title`)}
              </h2>
              <p className="mt-s2 text-gray-2 italic">{t(`tools.woop.fields.${field}.prompt`)}</p>
            </label>
            <textarea
              id={`woop-${field}`}
              value={form[field]}
              onChange={(event) => setForm((prev) => ({ ...prev, [field]: event.target.value }))}
              placeholder={t(`tools.woop.fields.${field}.placeholder`)}
              className="input-field mt-s3 min-h-24"
              data-testid={`woop-${field}`}
              rows={4}
            />
          </article>
        ))}

        {submitError && (
          <p role="alert" className="form-error" data-testid="woop-submit-error">
            {submitError}
          </p>
        )}

        {savedAt && (
          <p role="status" className="text-sm text-flame-deep" data-testid="woop-saved-at">
            ✓ {t('tools.woop.savedAt', { when: savedAt.slice(0, 19).replace('T', ' ') })}
          </p>
        )}

        <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
          {submitting ? t('tools.woop.submitting') : t('tools.woop.submit')}
        </button>
        <FinalizeWarning />
      </form>
    </section>
  );
}
